import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Input, Skeleton } from '@/ui';
import { FormItem, IFormInstance, FileUpload, PhoneInput } from '@/components';
import { StyledWrapper } from '../Styled';
import { StyledForm, FormColumn, StyledFormColumnTitle } from './UpdatePartnerFormStyles';
import { UPDATE_PARTNER_FORM_NAME } from '../../containers/PartnerDetailsContainer/PartnerDetailsContainer';
import CompanyDataFields from '../CompanyDataFields/CompanyDataFields';
import { getPartnerFormInitialValues } from '../../helpers/companyData';
import CategoryManagerField from '../CategoryManagerField/CategoryManagerField';
import AccountantFields from '../AccountantFields/AccountantFields';
import BankAccountsFields from '../BankAccountsFields/BankAccountsFields';

// Models
import { IPartnerProfile } from '@/models/partners.model';

// Helpers
import { PartnerFormFieldsNames, TPartnerFormValues } from '../../helpers/types';
import { usePartnerFormRules } from '../../hooks';

interface IUpdatePartnerForm {
  handleSubmit: (values: TPartnerFormValues) => void;
  isLoading: boolean;
  formInstance: IFormInstance;
  initialValues?: IPartnerProfile | null;
}

const UpdatePartnerForm = ({ handleSubmit, isLoading, initialValues, formInstance }: IUpdatePartnerForm) => {
  const { t } = useTranslation(['partners', 'common']);
  const rules = usePartnerFormRules();

  const placeholder = t('common_placeholder_input', { ns: 'common' });

  if (isLoading) return <Skeleton />;

  const formInitialValues = getPartnerFormInitialValues(initialValues);

  return (
    <StyledWrapper>
      <StyledForm
        onFinish={handleSubmit}
        form={formInstance}
        initialValues={formInitialValues}
        name={UPDATE_PARTNER_FORM_NAME}
      >
        <FormColumn>
          <StyledFormColumnTitle>{t('partners_company_data')}</StyledFormColumnTitle>

          <CompanyDataFields initialValues={initialValues} />

          <StyledFormColumnTitle>{t('partners_bank_information')}</StyledFormColumnTitle>

          <BankAccountsFields initialValues={initialValues} />

          <StyledFormColumnTitle>{t('partners_accountant_contacts')}</StyledFormColumnTitle>

          <AccountantFields />
        </FormColumn>

        <FormColumn>
          <StyledFormColumnTitle>{t('partners_authorized_person_data')}</StyledFormColumnTitle>

          <FormItem
            name={PartnerFormFieldsNames.FIRST_NAME}
            label={t('partners_contact_person_name')}
            rules={rules[PartnerFormFieldsNames.FIRST_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.LAST_NAME}
            label={t('partners_contact_person_last_name')}
            rules={rules[PartnerFormFieldsNames.LAST_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.MIDDLE_NAME}
            label={t('partners_contact_person_middle_name')}
            rules={rules[PartnerFormFieldsNames.MIDDLE_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.EMAIL}
            label={t('partners_email')}
            rules={rules[PartnerFormFieldsNames.EMAIL]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.PHONE}
            label={t('partners_phone_number')}
            rules={rules[PartnerFormFieldsNames.PHONE]}
          >
            <PhoneInput />
          </FormItem>

          <FormItem
            name={PartnerFormFieldsNames.SUBSCRIBER_FULL_NAME}
            label={t('partners_subscriber_full_name')}
            rules={rules[PartnerFormFieldsNames.SUBSCRIBER_FULL_NAME]}
          >
            <Input placeholder={placeholder} />
          </FormItem>

          <FormItem name={PartnerFormFieldsNames.DOCUMENT} label={t('partners_document')}>
            <FileUpload />
          </FormItem>

          <StyledFormColumnTitle>{t('partners_additional_info')}</StyledFormColumnTitle>

          <CategoryManagerField
            label={t('partners_category_manager_optional')}
            currentManager={initialValues?.category_manager}
          />
        </FormColumn>
      </StyledForm>
    </StyledWrapper>
  );
};

export default UpdatePartnerForm;
